import { bodyPose, type Flair, flairFor, GLOW_PX, glowAt } from '@gardener/assets/flair';
import { drawGardener, drawWardrobe, GARDENER } from '@gardener/assets/gardener';
import {
  EMBERS,
  GUN_SKIN_PALETTES,
  MOTES,
  type Mote,
  type MoteShape,
  type MoteView,
  SKIN_FX,
  type SkinFx,
  type StreakStyle,
  spawnMote,
  stepMote,
  viewMote,
} from '@gardener/assets/gun-skins';
import { type PixelCanvas, renderFrame, type SpriteDef, ZZFX_SAMPLE_RATE } from '@gardener/assets/lib';
import { hexToRgba, PALETTE, type PaletteKey } from '@gardener/assets/palette';
import {
  COUNTER_BOX,
  type Painted,
  POLISH_RES,
  type PolishName,
  paintCount,
  paintPolished,
} from '@gardener/assets/polished';
import { type SfxName, sfxSamples } from '@gardener/assets/sfx';
import scarecrow from '@gardener/assets/sprites/actors/scarecrow.sprite';
import skinShots from '@gardener/assets/sprites/fx/effect-skins-fx.sprite';
import fx from '@gardener/assets/sprites/fx/fx.sprite';
import mythicShots from '@gardener/assets/sprites/fx/mythic-fx.sprite';
import props from '@gardener/assets/sprites/props/props.sprite';
import ground from '@gardener/assets/sprites/tiles/ground.sprite';
import effectGuns from '@gardener/assets/sprites/weapons/effect-guns.sprite';
import mythicGuns from '@gardener/assets/sprites/weapons/mythic-guns.sprite';
import pollinator from '@gardener/assets/sprites/weapons/pollinator.sprite';
import {
  GARDENER as CHARACTER,
  DEFAULT_LOOK,
  DEFAULT_WEAPON,
  type GunSkin,
  type HatKey,
  HEADS,
  type ItemKind,
  isEffectSkin,
  isGunSkin,
  isHeadKey,
  isPolishedSkin,
  isSetKey,
  type Look,
  SCARECROW,
  TICK_RATE,
} from '@gardener/content';

/**
 * Skins drawn by the admin app itself (ADR-0157; a gardener's too, ADR-0160), from the game's own art
 * (`@gardener/assets`): a skin as the Store's tile shows it (`drawTile`: the default gun in a gun skin, `gunPicture`; a
 * head or an outfit as the wardrobe pictures it), and a showcase of a gardener in it (`Showcase`), two ways: in action
 * (it runs an arc over a scarecrow firing bursts into it, stands sweeping its aim round a full turn, firing, and runs
 * back) and in the lobby (big on its bed in a spotlight, as PLAY's stage shows you: swaying, fidgeting, turned by a
 * drag). What a mythic piece does on its own (its flair) is the game's own engine (`@gardener/assets/flair`).
 *
 * ponytail: the gardener's rig (`PlayerView`), the shot's juice (`WeaponJuice.shot`, `Impacts`, `JUICE`), the bullets
 * (`Bullets`), the scarecrow's wobble (`ScarecrowView`) and the lobby's figure, bed and spotlight (`GardenerFigure`,
 * `life.ts`, `Bed`, `PartyStage`) are the client's, copied by hand (an app can't import another): a change there shows
 * here only once copied. The art and the skins' tables are shared, so a new skin shows
 * as the game draws it.
 */

const SPRITES = new Map<string, SpriteDef>(
  [pollinator, scarecrow, ...effectGuns, ...mythicGuns, ...fx, ...skinShots, ...mythicShots, ...props, ...ground].map(
    (d): [string, SpriteDef] => [d.name, d],
  ),
);

const spriteDef = (name: string): SpriteDef => {
  const d = SPRITES.get(name);
  if (!d) throw new Error(`no sprite ${name}`);
  return d;
};

const frames = new Map<string, PixelCanvas>();
/** Frame `i` of sprite `name`'s `anim`, drawn once. */
function frameOf(name: string, anim: string, i: number): PixelCanvas {
  const key = `${name}/${anim}/${i}`;
  let c = frames.get(key);
  if (!c) {
    c = renderFrame(spriteDef(name), anim, i);
    frames.set(key, c);
  }
  return c;
}

type Recolor = Partial<Record<PaletteKey, PaletteKey>>;
const RGBA: Record<string, number[]> = Object.fromEntries(
  Object.entries(PALETTE).map(([k, hex]) => [k, hexToRgba(hex)]),
);
const WHITE = [255, 255, 255, 255];

/** `c` as RGBA, recoloured by `map` (a skin's palette); `flash`: every pixel white (a hit's flash, the atlas's `@flash`). */
function rgba(c: PixelCanvas, map: Recolor = {}, flash = false): Painted {
  const out = new Uint8ClampedArray(c.w * c.h * 4);
  c.pixels.forEach((p, i) => {
    if (p !== null) out.set(flash ? WHITE : (RGBA[map[p] ?? p] as number[]), i * 4);
  });
  return { rgba: out, w: c.w, h: c.h };
}

/** `rgba`'s colours times `tint`'s (as a Pixi sprite's tint), in place. */
function multiply(rgba: Uint8ClampedArray, tint: number): void {
  const k = [(tint >> 16) & 255, (tint >> 8) & 255, tint & 255];
  for (let i = 0; i < rgba.length; i++) if (i % 4 !== 3) rgba[i] = ((rgba[i] as number) * (k[i % 4] as number)) / 255;
}

const polishOf = (skin: GunSkin) => skin.slice('polished_'.length) as PolishName;

/**
 * The default gun in `skin` as the game draws it (`gunAnim`, the Store's Gun tab): the Pollinator's frame recoloured
 * (`GUN_SKIN_PALETTES`), an effect skin's own gun (`SKIN_FX`), or a polished one painted smooth, `res` px an art px.
 * Always the Pollinator's 64×36 art px frame, its grip at the Pollinator's origin.
 */
export function gunPicture(skin: GunSkin, anim = 'idle', frame = 0): Painted & { res: number } {
  if (isPolishedSkin(skin)) return { ...paintPolished(polishOf(skin)), res: POLISH_RES };
  if (isEffectSkin(skin)) return { ...rgba(frameOf(SKIN_FX[skin].held, anim, frame)), res: 1 };
  const palette = (GUN_SKIN_PALETTES as Record<string, Recolor>)[skin] ?? {};
  return { ...rgba(frameOf('pollinator', anim, frame), palette), res: 1 };
}

const canvas = (w: number, h: number): HTMLCanvasElement =>
  Object.assign(document.createElement('canvas'), { width: w, height: h });
const ctx = (c: HTMLCanvasElement) => c.getContext('2d') as CanvasRenderingContext2D;
const put = (c: HTMLCanvasElement, p: Painted) =>
  ctx(c).putImageData(new ImageData(p.rgba as Uint8ClampedArray<ArrayBuffer>, p.w, p.h), 0, 0);

/** Jobs for idle moments, ~8 ms of them a turn (a polished gun takes ~15 ms to paint: a grid of them stays responsive). */
const jobs: (() => void)[] = [];
function later(job: () => void): void {
  jobs.push(job);
  if (jobs.length > 1) return;
  const drain = () => {
    const until = performance.now() + 8;
    while (jobs.length && performance.now() < until) jobs.shift()?.();
    if (jobs.length) setTimeout(drain, 0);
  };
  setTimeout(drain, 0);
}

/**
 * Paints the default gun in `skin` on `c` at its own size (a grid tile's picture): pixel art drawn square
 * (`image-rendering: pixelated`), a polished gun smooth, painted in an idle moment.
 */
export function drawGun(c: HTMLCanvasElement, skin: GunSkin): void {
  const paint = () => {
    const p = gunPicture(skin);
    Object.assign(c, { width: p.w, height: p.h });
    c.style.imageRendering = p.res > 1 ? 'auto' : 'pixelated';
    put(c, p);
  };
  if (isPolishedSkin(skin)) later(paint);
  else paint();
}

/** What the showcase's gardener wears: a hat over a look (content's), and the skin on its gun. */
export interface Worn {
  hat: HatKey;
  look: Look;
  gun: GunSkin;
}

/**
 * The gardener that shows skin `key` of `kind`: the default one (the straw hat, the overalls, a stock gun) in it;
 * `whole`: in the whole set it is a piece of (its head, its suit and its gun, each keyed like the set).
 */
export function wornFor(kind: ItemKind, key: string, whole = false): Worn {
  const set = whole && isSetKey(key);
  const head = (kind === 'head' || set) && isHeadKey(key) ? HEADS[key] : null;
  return {
    hat: head?.hat ?? 'straw',
    look: { ...DEFAULT_LOOK, ...head?.face, ...(kind === 'outfit' || set ? { outfit: key as Look['outfit'] } : {}) },
    gun: (kind === 'gun' || set) && isGunSkin(key) ? key : 'stock',
  };
}

/**
 * A head or an outfit as the wardrobe pictures it (`drawWardrobe`: the head down to the collar, the clothes alone), cut
 * to what is drawn.
 */
export function wardrobePicture(kind: 'head' | 'outfit', key: string): Painted {
  const { hat, look } = wornFor(kind, key);
  const c = drawWardrobe(look, hat, 'blue', kind === 'head' ? 'head' : 'clothes');
  let [x0, y0, x1, y1] = [c.w, c.h, 0, 0];
  c.pixels.forEach((px, i) => {
    if (px === null) return;
    const [x, y] = [i % c.w, Math.floor(i / c.w)];
    [x0, y0, x1, y1] = [Math.min(x0, x), Math.min(y0, y), Math.max(x1, x + 1), Math.max(y1, y + 1)];
  });
  const [w, h] = [Math.max(1, x1 - x0), Math.max(1, y1 - y0)];
  const all = rgba(c);
  const out = new Uint8ClampedArray(w * h * 4);
  for (let y = 0; y < h; y++)
    out.set(all.rgba.subarray(((y0 + y) * c.w + x0) * 4, ((y0 + y) * c.w + x0 + w) * 4), y * w * 4);
  return { rgba: out, w, h };
}

/** A tile's picture is this big at most, CSS px: a gun's 64×36 art px twice over, a head's 50 rows twice over. */
const TILE = { w: 128, h: 100 } as const;

/**
 * Paints skin `key` of `kind` on `c` as its tile shows it: the default gun in a gun skin (`drawGun`), a head or an
 * outfit as the wardrobe pictures it, as big as the tile takes at a whole number of px an art px.
 */
export function drawTile(c: HTMLCanvasElement, kind: ItemKind, key: string): void {
  if (kind === 'gun') {
    if (isGunSkin(key)) drawGun(c, key);
    return;
  }
  const p = wardrobePicture(kind, key);
  Object.assign(c, { width: p.w, height: p.h });
  const k = Math.max(1, Math.floor(Math.min(TILE.w / p.w, TILE.h / p.h)));
  Object.assign(c.style, { imageRendering: 'pixelated', width: `${p.w * k}px`, height: `${p.h * k}px` });
  put(c, p);
}

/** Canvas px an art px a frame is kept at, drawn smooth: its texels stay sharp squares at any scale (`render/sharp.ts`). */
const UP = 4;

/** A frame to draw: its picture (`UP` px an art px), its size and origin in art px, its art px a world px. */
interface Art {
  img: HTMLCanvasElement;
  w: number;
  h: number;
  ox: number;
  oy: number;
  d: number;
}

/** `p` (a picture `res` px an art px) as `Art` with origin (ox, oy) at density `d`. */
function artOf(p: Painted, ox: number, oy: number, d: number, res = 1): Art {
  const src = canvas(p.w, p.h);
  put(src, p);
  const w = p.w / res;
  const h = p.h / res;
  if (res > 1) return { img: src, w, h, ox, oy, d };
  const img = canvas(w * UP, h * UP);
  const g = ctx(img);
  g.imageSmoothingEnabled = false;
  g.drawImage(src, 0, 0, img.width, img.height);
  return { img, w, h, ox, oy, d };
}

const arts = new Map<string, Art[]>();
/** Sprite `name`'s `anim` as `Art`, tinted (`tint`, as Pixi's) or as its white flash. */
function art(name: string, anim: string, tint = 0xffffff, flash = false): Art[] {
  const key = `${name}/${anim}/${tint}/${flash}`;
  let a = arts.get(key);
  if (!a) {
    const d = spriteDef(name);
    a = (d.anims[anim]?.frames ?? []).map((_, i) => {
      const p = rgba(frameOf(name, anim, i), {}, flash);
      if (tint !== 0xffffff) multiply(p.rgba, tint);
      return artOf(p, d.origin?.[0] ?? 0, d.origin?.[1] ?? 0, d.density ?? 1);
    });
    arts.set(key, a);
  }
  return a;
}
const fpsOf = (name: string, anim: string) => spriteDef(name).anims[anim]?.fps ?? 0;
const pick = <T>(xs: readonly T[], i: number): T => xs[Math.min(xs.length - 1, Math.max(0, i))] as T;

interface GardenerArt {
  idle: Art[];
  run: Art[];
  hand: Art;
  /** The idle frames darkened, for the lobby figure's back slices (`SHADES`), each made the first time it is drawn. */
  shade(frame: number, level: number): Art;
}

/** The lobby figure's back slices' brightness, back to front (`GardenerFigure`'s BACK … BACK + BACK_SPAN, in steps). */
const SHADES = [0.42, 0.52, 0.62, 0.7] as const;

/** A gardener's art in `hat` over `look`, blue (the Store's preview); its fist on every grip. */
function gardenerArt(hat: HatKey, look: Look): GardenerArt {
  const f = drawGardener(look, hat, 'blue');
  const { body, hand } = GARDENER;
  const of = (p: Painted, [ox, oy]: readonly number[], d: number) => artOf(p, ox as number, oy as number, d);
  const shaded = new Map<number, Art>();
  return {
    idle: f.idle.map((c) => of(rgba(c), body.origin, body.density)),
    run: f.run.map((c) => of(rgba(c), body.origin, body.density)),
    hand: of(rgba(f.hand[0] as PixelCanvas), hand.origin, hand.density),
    shade(frame, level) {
      const key = frame * SHADES.length + level;
      let a = shaded.get(key);
      if (!a) {
        const p = rgba(f.idle[frame % f.idle.length] as PixelCanvas);
        const v = Math.round((SHADES[level] as number) * 255);
        multiply(p.rgba, (v << 16) | (v << 8) | v);
        a = of(p, body.origin, body.density);
        shaded.set(key, a);
      }
      return a;
    },
  };
}

/** A damped spring (the client's `fx/spring.ts`). */
class Spring {
  value: number;
  velocity = 0;
  readonly target: number;
  private readonly stiffness: number;
  private readonly damping: number;
  constructor(value: number, stiffness: number, damping: number) {
    this.value = value;
    this.target = value;
    this.stiffness = stiffness;
    this.damping = damping;
  }
  update(dt: number): number {
    for (let left = Math.min(dt, 0.1); left > 0; left -= 1 / 120) {
      const h = Math.min(left, 1 / 120);
      this.velocity += (-this.stiffness * (this.value - this.target) - this.damping * this.velocity) * h;
      this.value += this.velocity * h;
    }
    return this.value;
  }
}

/** A particle (the client's `ParticleSystem`): fake height, bounce, and a rest on the ground before it fades. */
interface Bit {
  art: Art[];
  fps: number;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  gravity: number;
  drag: number;
  life: number;
  fade: boolean;
  scaleFrom: number;
  scaleTo: number;
  rotation: number;
  spin: number;
  alpha: number;
  add: boolean;
  bounce: number;
  rest: number;
  centre: boolean;
  age: number;
  resting: number;
  landed: boolean;
  frame: number;
}
type BitSpec = Partial<Omit<Bit, 'age' | 'resting' | 'landed' | 'frame'>> & Pick<Bit, 'art' | 'x' | 'y' | 'life'>;

/** A bullet in flight on the ground plane, drawn `AIM_HEIGHT` up; where along its way it drops its next ember. */
interface Shot {
  x: number;
  y: number;
  vx: number;
  vy: number;
  flown: number;
  age: number;
  next: number;
  every: number;
}

/** A hot streak's tail catching up into where it ended (`StreakFx.end`), drawn `AIM_HEIGHT` up. */
interface Spent {
  x: number;
  y: number;
  ux: number;
  uy: number;
  length: number;
  t: number;
  dur: number;
}

/** The gun's height over the feet, where bullets fly (`AIM_HEIGHT`). */
const AIM_HEIGHT = 5;
const W = DEFAULT_WEAPON;
const SHOT_EVERY = W.fireIntervalTicks / TICK_RATE;
/**
 * The scarecrow stands at the origin; the gardener runs the upper arc of this ellipse round it (world px), a little past
 * its sides, end to end: aiming at it from there the gun is always in front (aiming up, a gun goes behind its gardener).
 */
const ARC = { rx: 40, ry: 30, cy: 2, from: Math.PI - 0.35, to: 2 * Math.PI + 0.35 };
/** Where on the arc `k` of the way from its left end is. */
const arcAt = (k: number): [number, number] => {
  const theta = ARC.from + (ARC.to - ARC.from) * k;
  return [Math.cos(theta) * ARC.rx, ARC.cy + Math.sin(theta) * ARC.ry];
};
/** Seconds to run the arc at the gardener's top speed (its length summed in small steps). */
const ARC_S = (() => {
  let length = 0;
  for (let i = 0; i < 64; i++) {
    const [a, b] = [arcAt(i / 64), arcAt((i + 1) / 64)];
    length += Math.hypot(b[0] - a[0], b[1] - a[1]);
  }
  return length / CHARACTER.moveSpeed;
})();
/**
 * The loop (s): the arc run left to right firing a burst, a stand at its right end sweeping the aim round a full turn
 * firing (every angle, behind the back too), the arc run back, a stand at its left end firing a burst.
 */
const SWEEP_S = 2.6;
const REST_S = 1.1;
const LOOP_S = 2 * ARC_S + SWEEP_S + REST_S;
/** World px across the canvas, and where it looks. */
const VIEW = { w: 128, x: 0, y: -13 };
const GOLD = 0xfee761;
/**
 * The default gun's hot streak (`WEAPON_FX.pollinator.streak`, `JUICE.hotStreak`, the client's `render/fx/streak.ts`):
 * its flight's seconds long, its glow, core, tip and head (world px), an ember every `emberEvery` px on average.
 */
const STREAK = {
  seconds: 0.085,
  glow: 3.6,
  glowAlpha: 0.65,
  core: 1.6,
  coreLight: 0.45,
  tip: 14,
  head: 2.6,
  emberEvery: 30,
};
/** `c` (0xrrggbb) in alpha `a`, `k` of the way to white. */
const rgbaOf = (c: number, a: number, k = 0) => {
  const ch = (s: number) => Math.round(((c >> s) & 255) + (255 - ((c >> s) & 255)) * k);
  return `rgba(${ch(16)},${ch(8)},${ch(0)},${a})`;
};
/** A polished gun's counter shows this many kills (an owner's, as on PLAY's stage). */
const KILLS = 128;

/** The showcase's two views of a skin (ADR-0160): in a fight, or big on its bed in the lobby's spotlight. */
export type View = 'action' | 'lobby';

/**
 * The lobby view (PLAY's stage, `GardenerFigure` and `PartyStage`): the figure's height and its feet's place as shares
 * of the canvas, its fake depth (slices, and the stack's depth as a share of its height), its sway round its vertical
 * axis (degrees, and a half swing's seconds), its idle frame's seconds, and how a drag turns it (the share of its width a
 * radian of pull takes, the turn it follows freely and the most it ever shows, degrees, its lean per degree).
 */
const LOBBY = {
  h: 0.5,
  feet: [0.5, 0.82],
  slices: 14,
  depth: 0.1,
  sway: 22,
  swayS: 7.5,
  frameS: 0.4,
  grip: 0.35,
  soft: 35,
  max: 50,
  lean: 0.08,
} as const;
/** The gun's grip from the feet, body art px (`GardenerFigure`'s HOLD), and the swing its flair takes from a turn. */
const HOLD = { x: 4, y: -16 } as const;
const SWING = 16;

/** A pose round the figure's feet (`life.ts`): a hop (shares of its height), a squash, a lean and a turn (degrees). */
interface Pose {
  y: number;
  sx: number;
  sy: number;
  rot: number;
  turn: number;
}
const REST: Pose = { y: 0, sx: 1, sy: 1, rot: 0, turn: 0 };
/** What the figure does by itself now and then (`life.ts`'s moves): its length (s) and its key poses at 0..1. */
const MOVES: readonly { s: number; keys: readonly (readonly [number, Partial<Pose>])[] }[] = [
  // A hop.
  {
    s: 0.62,
    keys: [
      [0, {}],
      [0.18, { sx: 1.06, sy: 0.92 }],
      [0.45, { y: -0.08, sx: 0.96, sy: 1.05 }],
      [0.78, { sx: 1.05, sy: 0.94 }],
      [1, {}],
    ],
  },
  // A look behind and back.
  {
    s: 1.9,
    keys: [
      [0, {}],
      [0.1, {}],
      [0.24, { turn: 180 }],
      [0.56, { turn: 180 }],
      [0.7, {}],
      [1, {}],
    ],
  },
  // A shimmy.
  {
    s: 0.9,
    keys: [
      [0, {}],
      [0.2, { rot: -5 }],
      [0.45, { rot: 5 }],
      [0.7, { rot: -4 }],
      [1, {}],
    ],
  },
  // A stretch.
  {
    s: 1.5,
    keys: [
      [0, {}],
      [0.35, { sx: 0.95, sy: 1.08 }],
      [0.7, { sx: 0.95, sy: 1.08 }],
      [0.85, { sx: 1.03, sy: 0.97 }],
      [1, {}],
    ],
  },
  // A full turn, then a dizzy wobble.
  {
    s: 1.4,
    keys: [
      [0, {}],
      [0.5, { turn: 360 }],
      [0.62, { turn: 360, rot: -6 }],
      [0.75, { turn: 360, rot: 5 }],
      [0.88, { turn: 360, rot: -3 }],
      [1, { turn: 360 }],
    ],
  },
];

/** Move `m`'s pose `t` (0..1) of the way through: eased, straight lines between its key poses. */
function poseAt(m: (typeof MOVES)[number], t: number): Pose {
  const c = Math.max(0, Math.min(1, t));
  const e = c < 0.5 ? 2 * c * c : 1 - (-2 * c + 2) ** 2 / 2;
  let i = 0;
  while (i < m.keys.length - 2 && (m.keys[i + 1]?.[0] ?? 1) <= e) i++;
  const [o0, a] = m.keys[i] as readonly [number, Partial<Pose>];
  const [o1, b] = m.keys[i + 1] as readonly [number, Partial<Pose>];
  const f = o1 > o0 ? Math.max(0, Math.min(1, (e - o0) / (o1 - o0))) : 1;
  const lerp = (k: keyof Pose) => (a[k] ?? REST[k]) + ((b[k] ?? REST[k]) - (a[k] ?? REST[k])) * f;
  return { y: lerp('y'), sx: lerp('sx'), sy: lerp('sy'), rot: lerp('rot'), turn: lerp('turn') };
}

/** A flair's glow (`glowAt`'s rings) in white, tinted where it is drawn. */
let glowPicture: HTMLCanvasElement | null = null;
let glowTint: HTMLCanvasElement | null = null;
function glowIn(rgb: string): HTMLCanvasElement {
  if (!glowPicture) {
    const data = new Uint8ClampedArray(GLOW_PX * GLOW_PX * 4);
    for (let y = 0; y < GLOW_PX; y++)
      for (let x = 0; x < GLOW_PX; x++) {
        const at = (y * GLOW_PX + x) * 4;
        data.fill(255, at, at + 3);
        data[at + 3] = Math.round(
          glowAt(Math.hypot(x + 0.5 - GLOW_PX / 2, y + 0.5 - GLOW_PX / 2) / (GLOW_PX / 2)) * 255,
        );
      }
    glowPicture = canvas(GLOW_PX, GLOW_PX);
    put(glowPicture, { rgba: data, w: GLOW_PX, h: GLOW_PX });
    glowTint = canvas(GLOW_PX, GLOW_PX);
  }
  const t = ctx(glowTint as HTMLCanvasElement);
  t.globalCompositeOperation = 'copy';
  t.drawImage(glowPicture, 0, 0);
  t.globalCompositeOperation = 'source-in';
  t.fillStyle = rgb;
  t.fillRect(0, 0, GLOW_PX, GLOW_PX);
  return glowTint as HTMLCanvasElement;
}

const hex = (rgb: number) => `#${rgb.toString(16).padStart(6, '0')}`;

/** Each mote's mask (`MOTES`) in white, and a canvas to tint one in where it is drawn (as `glowIn`). */
const motePictures = new Map<MoteShape, HTMLCanvasElement>();
let moteTint: HTMLCanvasElement | null = null;
function moteIn(shape: MoteShape, rgb: string): HTMLCanvasElement {
  const mask = MOTES[shape];
  let picture = motePictures.get(shape);
  if (!picture) {
    const data = new Uint8ClampedArray(mask.w * mask.h * 4);
    for (let y = 0; y < mask.h; y++)
      for (let x = 0; x < mask.w; x++) {
        const at = (y * mask.w + x) * 4;
        data.fill(255, at, at + 3);
        data[at + 3] = Math.round(Math.max(0, Math.min(1, mask.a(x, y))) * 255);
      }
    picture = canvas(mask.w, mask.h);
    put(picture, { rgba: data, w: mask.w, h: mask.h });
    motePictures.set(shape, picture);
  }
  moteTint ??= canvas(32, 32);
  moteTint.width = mask.w;
  moteTint.height = mask.h;
  const t = ctx(moteTint);
  t.globalCompositeOperation = 'copy';
  t.drawImage(picture, 0, 0);
  t.globalCompositeOperation = 'source-in';
  t.fillStyle = rgb;
  t.fillRect(0, 0, mask.w, mask.h);
  return moteTint;
}

let audio: AudioContext | null = null;
const sounds = new Map<SfxName, AudioBuffer>();

/**
 * The showcase: a gardener in what it is told to wear (`show`), the default gun in its skin in its hands, drawn on
 * `canvas` at its size and the screen's density, looping: in a fight on a patch of lawn (`action`) or big on its bed in
 * the lobby's spotlight (`lobby`, where a drag turns it). Silent until `sound` is on (the fight's sounds).
 */
export class Showcase {
  private readonly canvas: HTMLCanvasElement;
  private readonly g: CanvasRenderingContext2D;
  private worn: Worn = wornFor('gun', 'stock');
  private art: GardenerArt | null = null;
  /** What the mythic pieces it wears do on their own (ADR-0160), stepped and drawn as in the game. */
  private flair: Flair | null = null;
  private mode: View = 'action';
  /** The lobby: what is still (the dimmed garden, the light, the bed), painted once a size. */
  private backdrop: HTMLCanvasElement | null = null;
  /** The lobby figure's clocks: its idle frame's, its sway's, till its next fidget; the move it plays and how far in. */
  private clock = 0;
  private swayT = 2;
  private nextFidget = 2.5;
  private move: (typeof MOVES)[number] | null = null;
  private moveT = 0;
  /** A drag on it: held, the pull so far (degrees) and the pointer's last x; let go, it springs back. */
  private held = false;
  private pull = 0;
  private grabX = 0;
  private readonly bend = new Spring(0, 121, 12);
  /** Dust in the spotlight's beam: how far down it (0..1), across it (-1..1), its twinkle's phase. */
  private readonly motes = Array.from({ length: 18 }, () => ({
    t: Math.random(),
    s: Math.random() * 2 - 1,
    p: Math.random() * 6.3,
  }));
  private fx: SkinFx | null = null;
  private gun: Art[] = [];
  private fire: Art[] | null = null;
  private counter: Art | null = null;
  private lawn: HTMLCanvasElement | null = null;
  private raf = 0;
  private last = 0;
  /** Seconds into the loop, the trigger last pulled, the gun's anim (`fire` once a shot: the angel's wings). */
  private t = 0;
  private shotAt = -1;
  private gunT = 0;
  private firing = false;
  private bodyT = 0;
  private moving = false;
  private x = 0;
  private y = 0;
  private aim = 0;
  private step = 0;
  private readonly recoil = new Spring(0, 520, 24);
  private readonly squash = new Spring(1, 420, 16);
  private readonly wobble = new Spring(0, 170, 6);
  private readonly sway = new Spring(1, 380, 11);
  private flashLeft = 0;
  private shots: Shot[] = [];
  private spent: Spent[] = [];
  /** The motes off the shots' streaks (`motes.ts`, the client's `StreakFx`: drawn where they are, up in the air). */
  private embers: Mote[] = [];
  private readonly moteView: MoteView = {
    shape: 'dot',
    x: 0,
    y: 0,
    rotation: 0,
    w: 0,
    h: 0,
    alpha: 0,
    tint: 0,
    solid: false,
  };
  private bits: Bit[] = [];
  private muted = true;
  private readonly sized = new ResizeObserver(() => this.resize());

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.g = ctx(canvas);
    this.sized.observe(canvas);
    // The lobby's gardener is turned by a drag, as PLAY's (`GardenerFigure.press` / `drag` / `drop`).
    canvas.addEventListener('pointerdown', (e) => {
      if (this.mode !== 'lobby') return;
      this.held = true;
      this.grabX = e.clientX;
      this.pull = this.bend.value;
      canvas.setPointerCapture(e.pointerId);
    });
    canvas.addEventListener('pointermove', (e) => {
      if (!this.held) return;
      const wide = (canvas.clientHeight * LOBBY.h * GARDENER.body.size[0]) / GARDENER.body.size[1];
      this.pull += ((e.clientX - this.grabX) / (LOBBY.grip * wide)) * (180 / Math.PI);
      this.grabX = e.clientX;
    });
    for (const up of ['pointerup', 'pointercancel'] as const)
      canvas.addEventListener(up, () => {
        this.held = false;
        this.bend.velocity = 0;
      });
  }

  /** Which way it is shown; the lobby's gardener can be grabbed. */
  set view(view: View) {
    this.mode = view;
    this.canvas.style.cursor = view === 'lobby' ? 'grab' : '';
    this.canvas.style.touchAction = view === 'lobby' ? 'none' : '';
  }

  /** Plays out loud (its sounds are the game's); turned on from a click, which lets the page start its audio. */
  set sound(on: boolean) {
    this.muted = !on;
    if (!on) return;
    audio ??= new AudioContext();
    void audio.resume();
  }

  /** Shows a gardener in `worn`, from the loop's start. */
  show(worn: Worn): void {
    const skin = worn.gun;
    this.worn = worn;
    this.art = gardenerArt(worn.hat, worn.look);
    this.flair = flairFor(worn.hat, worn.look.outfit, worn.gun, 7);
    this.fx = isEffectSkin(skin) ? SKIN_FX[skin] : null;
    if (isPolishedSkin(skin)) {
      const [ox, oy] = pollinator.origin;
      this.gun = [artOf(gunPicture(skin), ox, oy, pollinator.density, POLISH_RES)];
      const [x0, y0, x1, y1] = COUNTER_BOX;
      this.counter = artOf(paintCount(KILLS), (x1 - x0) / 2, (y1 - y0) / 2, pollinator.density, POLISH_RES);
      this.fire = null;
    } else {
      const held = this.fx?.held ?? 'pollinator';
      const n = spriteDef(held).anims.idle?.frames.length ?? 1;
      const of = (anim: string, i: number) =>
        artOf(gunPicture(skin, anim, i), pollinator.origin[0], pollinator.origin[1], pollinator.density);
      this.gun = Array.from({ length: n }, (_, i) => of('idle', i));
      const fire = this.fx ? (spriteDef(held).anims.fire?.frames.length ?? 0) : 0;
      this.fire = fire ? Array.from({ length: fire }, (_, i) => of('fire', i)) : null;
      this.counter = null;
    }
    this.t = 0;
    [this.x, this.y] = arcAt(0);
    this.step = 0;
    this.shotAt = -1;
    this.shots = [];
    this.spent = [];
    this.embers = [];
    this.bits = [];
    this.flashLeft = 0;
  }

  start(): void {
    if (this.raf) return;
    this.last = performance.now();
    const frame = (now: number) => {
      this.update(Math.min(0.1, (now - this.last) / 1000));
      this.last = now;
      this.draw();
      this.raf = requestAnimationFrame(frame);
    };
    this.raf = requestAnimationFrame(frame);
  }

  destroy(): void {
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.sized.disconnect();
  }

  private resize(): void {
    const k = devicePixelRatio || 1;
    const w = Math.round(this.canvas.clientWidth * k);
    const h = Math.round(this.canvas.clientHeight * k);
    if (!w || !h || (w === this.canvas.width && h === this.canvas.height && this.lawn)) return;
    Object.assign(this.canvas, { width: w, height: h });
    this.lawn = this.paintLawn();
    this.backdrop = this.paintLobby();
  }

  /** Device px a world px. */
  private get scale(): number {
    return this.canvas.width / VIEW.w;
  }

  /** The lawn under the view, painted once a size: grass tiles and decals picked by cell as the game's (`tilemap.ts`). */
  private paintLawn(): HTMLCanvasElement {
    const { width, height } = this.canvas;
    const lawn = canvas(width, height);
    const g = ctx(lawn);
    const s = this.scale;
    const [ox, oy] = [width / 2 - VIEW.x * s, height / 2 - VIEW.y * s];
    g.imageSmoothingEnabled = true;
    g.imageSmoothingQuality = 'high';
    const hash = (x: number, y: number, salt: number) => ((x * 73856093) ^ (y * 19349663) ^ (salt * 83492791)) >>> 0;
    const [tiles, decals] = [art('tile_grass', 'variants'), art('decal_grass', 'variants')];
    const half = { w: width / s / 2, h: height / s / 2 };
    const [x0, x1] = [Math.floor((VIEW.x - half.w) / 16), Math.ceil((VIEW.x + half.w) / 16)];
    const [y0, y1] = [Math.floor((VIEW.y - half.h) / 16), Math.ceil((VIEW.y + half.h) / 16)];
    // Each tile on whole device px: smooth edges on fractional ones would leave a seam between neighbours.
    const at = (v: number, o: number) => Math.round(v * 16 * s + o);
    for (let cy = y0; cy <= y1; cy++)
      for (let cx = x0; cx <= x1; cx++) {
        const { img } = pick(tiles, hash(cx, cy, 0) % tiles.length);
        g.drawImage(img, at(cx, ox), at(cy, oy), at(cx + 1, ox) - at(cx, ox), at(cy + 1, oy) - at(cy, oy));
      }
    g.setTransform(s, 0, 0, s, ox, oy);
    for (let cy = y0; cy <= y1; cy++)
      for (let cx = x0; cx <= x1; cx++)
        if (hash(cx, cy, 11) % 100 < 22)
          this.drawArt(g, pick(decals, hash(cx, cy, 12) % decals.length), cx * 16 + 8, cy * 16 + 8);
    return lawn;
  }

  // --- The loop ------------------------------------------------------------------------------------------------------

  private update(dt: number): void {
    if (this.mode === 'lobby') {
      this.live(dt);
      return;
    }
    this.t += dt;
    const u = this.t % LOOP_S;
    const back = 2 * ARC_S + SWEEP_S;
    /** Where on the arc, how far through the sweep (-1: not sweeping), and when the trigger is held. */
    let k = 0;
    let sweep = -1;
    let trigger = false;
    if (u < ARC_S) {
      k = u / ARC_S;
      trigger = u > 0.15 && u < ARC_S - 0.2;
    } else if (u < ARC_S + SWEEP_S) {
      k = 1;
      sweep = Math.min(1, Math.max(0, (u - ARC_S - 0.3) / (SWEEP_S - 0.6)));
      trigger = sweep > 0.03 && sweep < 0.97;
    } else if (u < back) {
      k = (back - u) / ARC_S;
      trigger = back - u > 0.2 && back - u < ARC_S - 0.15;
    } else trigger = u - back > 0.25 && u - back < 0.75;
    const [x, y] = arcAt(k);
    const moving = u < ARC_S || (u >= ARC_S + SWEEP_S && u < back);
    const atTarget = Math.atan2(-y, -x);
    // The sweep eased: a slow start, a slow stop.
    const aim = sweep < 0 ? atTarget : atTarget - Math.PI * 2 * sweep * sweep * (3 - 2 * sweep);
    // Footsteps every 15 px run (`JUICE.movement`), a squash as it starts and stops.
    const d = Math.hypot(x - this.x, y - this.y);
    if (moving !== this.moving) {
      this.squash.value = moving ? 1 - 0.18 : 1 + 0.18 * 0.6;
      this.bodyT = 0;
    }
    this.step += moving ? d : 0;
    if (this.step > 15) {
      this.step = 0;
      this.spawn({
        art: art('dust', 'puff'),
        fps: 12,
        x: x + (Math.random() - 0.5) * 4,
        y: y - 1,
        vx: (Math.random() - 0.5) * 10,
        vy: -6,
        life: 0.33,
        fade: false,
        alpha: 0.8,
      });
      this.play('footstep', 0.5, 0.25);
    }
    this.x = x;
    this.y = y;
    this.aim = aim;
    this.moving = moving;
    this.bodyT += dt;
    this.gunT += dt;
    if (this.firing && this.fire && this.gunT * 20 >= this.fire.length) this.firing = false;
    if (trigger && (this.shotAt < 0 || this.t - this.shotAt >= SHOT_EVERY)) {
      this.shotAt = this.t;
      this.shoot();
    }
    this.recoil.update(dt);
    this.squash.update(dt);
    this.wobble.update(dt);
    this.sway.update(dt);
    this.flashLeft = Math.max(0, this.flashLeft - dt);
    this.fly(dt);
    this.age(dt);
    // Its flair as the game steps it (`PlayerView`): from the feet in art px, the gun's from its grip.
    const px = GARDENER.body.density;
    const push = this.recoil.value;
    this.flair?.step(dt, {
      x: this.x * px,
      y: this.y * px,
      facing: this.facing as 1 | -1,
      body: bodyPose(moving ? 'run' : 'idle', this.bodyFrame),
      gun: {
        x: (this.facing + Math.cos(aim) * push) * px,
        y: (-AIM_HEIGHT + 1 + Math.sin(aim) * push) * px,
        aim,
      },
      team: 'blue',
    });
  }

  /** The body's frame now (its run or its idle). */
  private get bodyFrame(): number {
    const fps = this.moving ? GARDENER.body.fps.run : GARDENER.body.fps.idle;
    return Math.floor(this.bodyT * fps) % 6;
  }

  private get facing(): number {
    return Math.cos(this.aim) < 0 ? -1 : 1;
  }

  /** The muzzle as drawn (`PlayerView.muzzle`): at the gun's height, past the recoil. */
  private muzzle(): { x: number; y: number } {
    const reach = W.muzzleDistance - 1 + this.recoil.value;
    return {
      x: this.x + this.facing + Math.cos(this.aim) * reach,
      y: this.y - AIM_HEIGHT + Math.sin(this.aim) * reach,
    };
  }

  /** A shot (`WeaponJuice.shot`): the flash and its glow, a casing, the recoil and squash, the bullet, the sound. */
  private shoot(): void {
    const m = this.muzzle();
    const a = this.aim;
    const skin = this.fx;
    this.spawn({
      art: art(skin?.muzzle ?? 'muzzle_flash', 'flash'),
      fps: 30,
      ...m,
      life: 0.1,
      fade: false,
      rotation: a,
    });
    this.spawn({
      art: skin ? art('skin_glow', 'idle', skin.glow) : art('glow', 'idle'),
      x: m.x + Math.cos(a) * 4,
      y: m.y + Math.sin(a) * 4,
      life: 0.09,
      scaleFrom: 2.4 * 0.55 + 0.8,
      scaleTo: 0.2,
      add: true,
      alpha: 0.7,
      centre: true,
    });
    // An effect skin ejects no brass.
    if (!skin) {
      const side = Math.cos(a) >= 0 ? -1 : 1;
      const [px, py] = [-Math.sin(a) * side, Math.cos(a) * side];
      this.spawn({
        art: art('casing', 'idle'),
        x: m.x - Math.cos(a) * 8,
        y: m.y + AIM_HEIGHT - Math.sin(a) * 8,
        z: AIM_HEIGHT,
        vx: px * (40 + Math.random() * 30) - Math.cos(a) * 20,
        vy: py * (40 + Math.random() * 30) - Math.sin(a) * 20,
        vz: 70 + Math.random() * 40,
        gravity: 420,
        drag: 0.08,
        spin: (Math.random() - 0.5) * 40,
        bounce: 0.45,
        life: 1.2,
        rest: 2.5,
        fade: false,
      });
      this.play('casing', 0.6, 0.2);
    }
    this.recoil.value = -W.feel.recoil;
    this.squash.velocity += -1.2;
    if (this.fire) {
      this.firing = true;
      this.gunT = 0;
    }
    const angle = a + (Math.random() - 0.5) * W.spread;
    const reach = W.muzzleDistance - 1;
    this.shots.push({
      x: this.x + this.facing + Math.cos(angle) * reach,
      y: this.y + Math.sin(angle) * reach,
      vx: Math.cos(angle) * W.projectileSpeed,
      vy: Math.sin(angle) * W.projectileSpeed,
      flown: 0,
      age: 0,
      ...this.heat(),
    });
    this.play(skin?.sound ?? 'shoot', 0.9, 0.08);
  }

  /** The shots' hot streak's look: the skin's, else a plain gun's. */
  private get look(): StreakStyle {
    return { ...EMBERS, ...this.fx?.streak };
  }

  /** Where a new shot drops its first ember, and how far apart its embers come (each shot hotter or cooler). */
  private heat(): { next: number; every: number } {
    const every = STREAK.emberEvery / (0.45 + Math.random() * 1.1) / this.look.rate;
    return { next: 5 + Math.random() * every, every };
  }

  /** Bullets fly; one ends in the scarecrow (its hurt circle round the origin, `Bullets`, `Impacts`) or at its range. */
  private fly(dt: number): void {
    const r = SCARECROW.hurtRadius;
    for (const s of this.spent) s.t += dt;
    this.spent = this.spent.filter((s) => s.t < s.dur);
    this.embers = this.embers.filter((e) => stepMote(e, dt));
    this.shots = this.shots.filter((b) => {
      b.age += dt;
      const [dx, dy] = [b.vx * dt, b.vy * dt];
      // The first point of this step's segment inside the circle.
      const [fx, fy] = [b.x, b.y];
      const aa = dx * dx + dy * dy;
      const bb = 2 * (fx * dx + fy * dy);
      const disc = bb * bb - 4 * aa * (fx * fx + fy * fy - r * r);
      const t = disc < 0 ? -1 : fx * fx + fy * fy <= r * r ? 0 : (-bb - Math.sqrt(disc)) / (2 * aa);
      if (t >= 0 && t <= 1) {
        this.hit(fx + dx * t, fy + dy * t, Math.atan2(b.vy, b.vx));
        this.spend(b, fx + dx * t, fy + dy * t);
        return false;
      }
      b.x += dx;
      b.y += dy;
      b.flown += Math.hypot(dx, dy);
      while (b.flown >= b.next) {
        this.ember(b, b.flown - b.next + 3);
        b.next += Math.max(4, -Math.log(1 - Math.random()) * b.every);
      }
      // Its range's end is far out of the view (a pellet's drop, a skin's smaller burst): it just goes.
      return b.flown < W.rangePx - W.muzzleDistance;
    });
  }

  /** A hot streak ended at ground point (x, y): its tail catches up with it. */
  private spend(b: Shot, x: number, y: number): void {
    const length = Math.min(b.flown + 4, W.projectileSpeed * STREAK.seconds);
    const [ux, uy] = [b.vx / W.projectileSpeed, b.vy / W.projectileSpeed];
    this.spent.push({ x, y, ux, uy, length, t: 0, dur: Math.min(0.12, Math.max(0.04, length / W.projectileSpeed)) });
  }

  /** The motes off shot `b`'s way, `back` px behind it (`StreakFx.shed`: a look's `burst` at once). */
  private ember(b: Shot, back: number): void {
    const look = this.look;
    const [ux, uy] = [b.vx / W.projectileSpeed, b.vy / W.projectileSpeed];
    for (let i = 0; i < look.burst; i++)
      this.embers.push(
        spawnMote(look, b.x - ux * back, b.y - AIM_HEIGHT - uy * back, ux, uy, W.projectileSpeed, 1, Math.random),
      );
  }

  /** A bullet in the scarecrow: its spark or the skin's burst, the scarecrow's flash, wobble and straw. */
  private hit(x: number, y: number, angle: number): void {
    const skin = this.fx;
    if (skin) this.burst(skin, x, y, angle);
    else {
      this.spawn({ art: art('spark', 'idle'), fps: 10, x, y: y - AIM_HEIGHT, life: 0.15 });
      this.play('hit', 0.5, 0.12);
    }
    this.flashLeft = 0.09;
    this.wobble.velocity += (Math.cos(angle) >= 0 ? 1 : -1) * 9 * Math.min(2, 0.4 + W.damage / 12);
    this.sway.value = 0.86;
    // Fewer than the game's (5, resting 3 s): under burst after burst the lawn would be all straw.
    for (let i = 0; i < 2; i++) {
      const a = angle + (Math.random() - 0.5) * 1.4;
      const speed = 55 + Math.random() * 70;
      this.spawn({
        art: art('straw', 'variants'),
        x: 0,
        y: 0,
        z: 12 + Math.random() * 6,
        vx: Math.cos(a) * speed,
        vy: Math.sin(a) * speed * 0.6,
        vz: 40 + Math.random() * 60,
        gravity: 360,
        drag: 0.15,
        spin: (Math.random() - 0.5) * 20,
        bounce: 0.25,
        life: 1.5,
        rest: 1.5,
        fade: false,
      });
    }
  }

  /** An effect skin's shot ending in a target (`Impacts.skinEnded`): its burst, sparks flung back, its sound. */
  private burst(skin: SkinFx, x: number, y: number, angle: number): void {
    const frames = art(skin.burst, 'burst');
    this.spawn({ art: frames, fps: 20, x, y, z: AIM_HEIGHT, life: frames.length / 20, fade: false });
    for (let i = 0; skin.spark !== null && i < 3; i++) {
      const a = angle + Math.PI + (Math.random() - 0.5) * 2.4;
      const v = 40 + Math.random() * 50;
      this.spawn({
        art: art('spark', 'idle', skin.spark),
        fps: 8,
        x,
        y,
        z: AIM_HEIGHT,
        vx: Math.cos(a) * v,
        vy: Math.sin(a) * v * 0.6,
        vz: 20 + Math.random() * 30,
        gravity: 160,
        drag: 0.05,
        life: 0.3,
        add: true,
      });
    }
    this.play(skin.hit, 0.6, 0.12);
  }

  private spawn(s: BitSpec): void {
    this.bits.push({
      fps: 0,
      z: 0,
      vx: 0,
      vy: 0,
      vz: 0,
      gravity: 0,
      drag: 1,
      fade: true,
      scaleFrom: s.scaleFrom ?? 1,
      scaleTo: s.scaleTo ?? s.scaleFrom ?? 1,
      rotation: 0,
      spin: 0,
      alpha: 1,
      add: false,
      bounce: 0,
      rest: 0,
      centre: false,
      ...s,
      age: 0,
      resting: 0,
      landed: false,
      frame: s.fps ? 0 : Math.floor(Math.random() * s.art.length),
    });
  }

  /** The particles' lives (`ParticleSystem.update`). */
  private age(dt: number): void {
    this.bits = this.bits.filter((p) => {
      p.age += dt;
      if (!p.landed) {
        const keep = p.drag === 1 ? 1 : p.drag ** dt;
        p.vx *= keep;
        p.vy *= keep;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        if (p.gravity !== 0) {
          p.vz -= p.gravity * dt;
          p.z += p.vz * dt;
          if (p.z <= 0) {
            p.z = 0;
            if (p.bounce > 0 && Math.abs(p.vz) > 25) {
              p.vz = -p.vz * p.bounce;
              p.vx *= 0.6;
              p.vy *= 0.6;
              p.spin *= 0.5;
            } else if (p.rest > 0) p.landed = true;
            else p.vz = 0;
          }
        }
        p.rotation += p.spin * dt;
      } else p.resting += dt;
      if (p.fps) p.frame = Math.min(p.art.length - 1, Math.floor(p.age * p.fps));
      return p.landed ? p.resting < p.rest + 0.6 : p.age < p.life;
    });
  }

  private play(name: SfxName, volume: number, variance: number): void {
    if (this.muted || !audio) return;
    let buffer = sounds.get(name);
    if (!buffer) {
      const samples = sfxSamples(name);
      buffer = audio.createBuffer(1, samples.length, ZZFX_SAMPLE_RATE);
      buffer.copyToChannel(samples as Float32Array<ArrayBuffer>, 0);
      sounds.set(name, buffer);
    }
    const src = audio.createBufferSource();
    src.buffer = buffer;
    src.playbackRate.value = 1 + (Math.random() * 2 - 1) * variance;
    const gain = audio.createGain();
    gain.gain.value = volume * 0.5;
    src.connect(gain).connect(audio.destination);
    src.start();
  }

  // --- Drawing (the client's layers: ground, decals, shadows, actors by their feet, held guns, fx) --------------------

  private draw(): void {
    const g = this.g;
    const { width, height } = this.canvas;
    const me = this.art;
    if (!width || !this.lawn || !me) return;
    if (this.mode === 'lobby') {
      this.drawLobby(me);
      return;
    }
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.globalAlpha = 1;
    g.drawImage(this.lawn, 0, 0);
    const s = this.scale;
    g.setTransform(s, 0, 0, s, width / 2 - VIEW.x * s, height / 2 - VIEW.y * s);
    g.imageSmoothingEnabled = true;
    g.imageSmoothingQuality = 'high';
    for (const p of this.bits) if (p.landed) this.drawBit(p);
    const shadow = pick(art('shadow', 'idle'), 0);
    const sq = this.squash.value;
    this.drawArt(g, shadow, this.x, this.y, { sx: 2 - sq, alpha: 0.32, centre: true });
    this.drawArt(g, shadow, 0, 0, { alpha: 0.3, centre: true });
    // The gun goes behind its gardener aiming up; in front it rides over every actor.
    const behind = Math.sin(this.aim) < -0.35;
    const gardenerAt = () => {
      this.drawFlair(0, this.x, this.y);
      if (behind) this.drawGun(me);
      const body = this.moving ? me.run : me.idle;
      this.drawArt(g, pick(body, this.bodyFrame % body.length), this.x, this.y, { sx: this.facing * (2 - sq), sy: sq });
      this.drawFlair(1, this.x, this.y);
    };
    const scarecrowAt = () => {
      const sqs = this.sway.value;
      const o = { rot: this.wobble.value * 0.2, sx: 2 - sqs, sy: sqs };
      this.drawArt(g, pick(art('scarecrow', 'idle'), 0), 0, 0, o);
      // Half the game's flash: hit in bursts, a whole white scarecrow hides what it is.
      const flash = this.flashLeft > 0 ? Math.min(1, this.flashLeft / 0.04) * 0.5 : 0;
      if (flash) this.drawArt(g, pick(art('scarecrow', 'idle', 0xffffff, true), 0), 0, 0, { ...o, alpha: flash });
    };
    for (const draw of this.y < 0 ? [gardenerAt, scarecrowAt] : [scarecrowAt, gardenerAt]) draw();
    if (!behind) this.drawGun(me);
    for (const p of this.bits) if (!p.landed) this.drawBit(p);
    this.drawShots();
  }

  /**
   * One of the flair's layers (0 behind the body, 1 over it, 2 with the gun) from feet at (x, y) in `g`'s units, `sx` ×
   * `sy` of them a body art px (a quarter of a world px in the fight).
   */
  private drawFlair(layer: number, x: number, y: number, sx = 1 / GARDENER.body.density, sy = sx): void {
    const f = this.flair;
    if (!f) return;
    const g = this.g;
    g.save();
    g.translate(x, y);
    g.scale(sx, sy);
    g.imageSmoothingEnabled = false;
    for (let i = 0; i < f.count; i++) {
      const d = f.draws[i];
      if (!d || d.layer !== layer) continue;
      g.globalAlpha = d.a;
      g.globalCompositeOperation = d.add ? 'lighter' : 'source-over';
      if (d.glow) g.drawImage(glowIn(hex(d.rgb)), d.x - d.w / 2, d.y - d.h / 2, d.w, d.h);
      else {
        g.fillStyle = hex(d.rgb);
        g.fillRect(d.x, d.y, d.w, d.h);
      }
    }
    g.restore();
  }

  /** The gun and the fist on its grip, turning as one round the shoulder (`PlayerView`'s rig), mirrored aiming left. */
  private drawGun(me: GardenerArt): void {
    const g = this.g;
    const f = this.facing;
    const push = this.recoil.value;
    g.save();
    g.translate(this.x + f + Math.cos(this.aim) * push, this.y - AIM_HEIGHT + 1 + Math.sin(this.aim) * push);
    g.rotate(this.aim);
    g.scale(1, f);
    const frames = this.firing && this.fire ? this.fire : this.gun;
    const fps = this.firing ? 20 : this.fx ? fpsOf(this.fx.held, 'idle') : 0;
    this.drawArt(g, pick(frames, fps ? Math.floor(this.gunT * fps) % frames.length : 0), 0, 0);
    if (this.counter) {
      const [x0, y0, x1, y1] = COUNTER_BOX;
      const [ox, oy] = pollinator.origin;
      const d = pollinator.density;
      this.drawArt(g, this.counter, ((x0 + x1) / 2 - ox) / d, ((y0 + y1) / 2 - oy) / d, { sx: f });
    }
    const [hx, hy] = pollinator.hand;
    const [ox, oy] = pollinator.origin;
    this.drawArt(g, me.hand, (hx - ox) / pollinator.density, (hy - oy) / pollinator.density);
    g.restore();
    // Its own flair over it, wherever it is drawn.
    this.drawFlair(2, this.x, this.y);
  }

  // --- The lobby (PLAY's stage: `PartyStage`'s spotlight, `Bed`, `GardenerFigure`) ---------------------------------------

  /** Where the lobby's gardener stands on the canvas and how big: its feet (device px) and device px a body art px. */
  private get stand(): { x: number; y: number; k: number; h: number } {
    const { width, height } = this.canvas;
    const h = height * LOBBY.h;
    return { x: width * LOBBY.feet[0], y: height * LOBBY.feet[1], k: h / GARDENER.body.size[1], h };
  }

  /**
   * What is still in the lobby, painted once a size: the garden dimmed to dusk, darker away from the gardener, the
   * spotlight's beam from the top down to a warm pool at its feet, a halo behind it, and its bed.
   */
  private paintLobby(): HTMLCanvasElement {
    const { width, height } = this.canvas;
    const back = canvas(width, height);
    const g = ctx(back);
    const { x, y, h } = this.stand;
    if (this.lawn) g.drawImage(this.lawn, 0, 0);
    g.fillStyle = 'rgba(9,10,22,0.6)';
    g.fillRect(0, 0, width, height);
    const dim = g.createRadialGradient(x, y - h * 0.5, 0, x, y - h * 0.5, Math.max(width, height) * 0.75);
    dim.addColorStop(0, 'rgba(9,10,22,0)');
    dim.addColorStop(0.3, 'rgba(9,10,22,0.12)');
    dim.addColorStop(1, 'rgba(9,10,22,0.6)');
    g.fillStyle = dim;
    g.fillRect(0, 0, width, height);
    // The beam: three layers from the lamp to the floor, each narrower (a soft edge, a bright core).
    g.globalCompositeOperation = 'lighter';
    const beam = g.createLinearGradient(0, 0, 0, y);
    beam.addColorStop(0, 'rgba(255,238,190,0.2)');
    beam.addColorStop(0.7, 'rgba(255,238,190,0.07)');
    beam.addColorStop(1, 'rgba(255,238,190,0.03)');
    g.fillStyle = beam;
    for (const k of [1, 0.78, 0.55]) {
      g.beginPath();
      g.moveTo(x - 0.2 * h * k, 0);
      g.lineTo(x + 0.2 * h * k, 0);
      g.lineTo(x + 0.95 * h * k, y);
      g.lineTo(x - 0.95 * h * k, y);
      g.fill();
    }
    const halo = g.createRadialGradient(x, y - h * 0.5, 0, x, y - h * 0.5, h * 0.75);
    halo.addColorStop(0, 'rgba(255,236,186,0.22)');
    halo.addColorStop(1, 'rgba(255,236,186,0)');
    g.fillStyle = halo;
    g.fillRect(0, 0, width, height);
    g.save();
    g.translate(x, y);
    g.scale(1.15, 0.3);
    const pool = g.createRadialGradient(0, 0, 0, 0, 0, h);
    pool.addColorStop(0, 'rgba(255,240,196,0.5)');
    pool.addColorStop(0.55, 'rgba(255,240,196,0.2)');
    pool.addColorStop(1, 'rgba(255,240,196,0)');
    g.fillStyle = pool;
    g.fillRect(-h, -h, 2 * h, 2 * h);
    g.restore();
    g.globalCompositeOperation = 'source-over';
    this.paintBed(g, x, y, h * 1.1);
    return back;
  }

  /** The round garden bed a gardener stands on (`Bed`: the prototype's 200 × 88 box, `u` = width / 200), gold-rimmed. */
  private paintBed(g: CanvasRenderingContext2D, x: number, y: number, w: number): void {
    const u = w / 200;
    const [rx, ry, side] = [94 * u, 27 * u, 20 * u];
    const ink = '#181425';
    g.save();
    g.translate(x, y);
    g.lineWidth = Math.max(1, 3.5 * u);
    g.lineJoin = 'round';
    g.fillStyle = 'rgba(24,20,37,0.28)';
    g.beginPath();
    g.ellipse(0, side + 2.5 * u, rx + 2 * u, ry, 0, 0, Math.PI * 2);
    g.fill();
    // The dirt: the side down from the top's ends, round the bottom half of an ellipse `side` lower.
    const dirt = g.createLinearGradient(0, 0, 0, side + ry);
    dirt.addColorStop(0, '#8f7d4c');
    dirt.addColorStop(1, '#5e4f2e');
    g.fillStyle = dirt;
    g.strokeStyle = ink;
    g.beginPath();
    g.moveTo(-rx, 0);
    g.ellipse(0, side, rx, ry, 0, Math.PI, 0, true);
    g.lineTo(rx, 0);
    g.closePath();
    g.fill();
    g.stroke();
    // The top: grass lit from a little above its middle, a gold rim inside its ink one, a gloss.
    const grass = g.createRadialGradient(0, -ry * 0.24, 0, 0, -ry * 0.24, rx * 1.24);
    grass.addColorStop(0, '#8e9230');
    grass.addColorStop(0.6, '#72751b');
    grass.addColorStop(1, '#4c5014');
    g.fillStyle = grass;
    g.beginPath();
    g.ellipse(0, 0, rx, ry, 0, 0, Math.PI * 2);
    g.fill();
    g.stroke();
    g.strokeStyle = '#feae34';
    g.globalAlpha = 0.9;
    g.lineWidth = Math.max(1, 4 * u);
    g.beginPath();
    g.ellipse(0, 0, 88 * u, 22 * u, 0, 0, Math.PI * 2);
    g.stroke();
    g.globalAlpha = 1;
    g.fillStyle = 'rgba(255,255,255,0.1)';
    g.beginPath();
    g.ellipse(0, -4 * u, 66 * u, 13 * u, 0, 0, Math.PI * 2);
    g.fill();
    // The contact shadow the gardener stands in.
    g.fillStyle = 'rgba(24,20,37,0.32)';
    g.beginPath();
    g.ellipse(0, 3 * u, 40 * u, 9 * u, 0, 0, Math.PI * 2);
    g.fill();
    g.restore();
  }

  /** The lobby figure's turn as shown (degrees) for a pull of `deg`: as pulled up to `soft`, then rubber-banded to `max`. */
  private turned(deg: number): number {
    const a = Math.abs(deg);
    if (a <= LOBBY.soft) return deg;
    const room = LOBBY.max - LOBBY.soft;
    return Math.sign(deg) * (LOBBY.soft + room * (1 - Math.exp((LOBBY.soft - a) / room)));
  }

  /** The figure's sway now, degrees. */
  private get swayNow(): number {
    return -LOBBY.sway * Math.cos((Math.PI * this.swayT) / LOBBY.swayS);
  }

  /** The move it plays now, as a pose (at rest: none). */
  private get pose(): Pose {
    return this.move ? poseAt(this.move, this.moveT / this.move.s) : REST;
  }

  /** The lobby's gardener lives (`GardenerFigure.update`): it breathes, sways, fidgets now and then, follows a drag. */
  private live(dt: number): void {
    this.clock += dt;
    if (this.held) this.bend.value = this.turned(this.swayNow + this.pull) - this.swayNow;
    else this.bend.update(dt);
    // Turned by hand, its sway and its fidgets wait.
    const handled = this.held || Math.abs(this.bend.value) > 0.5;
    if (!handled) {
      this.swayT += dt;
      this.nextFidget -= dt;
    }
    if (this.move) {
      this.moveT += dt;
      if (this.moveT >= this.move.s) this.move = null;
    } else if (this.nextFidget <= 0) {
      // A spin once in a while, the small moves the rest of the time.
      this.move = pick(MOVES, Math.random() < 0.22 ? MOVES.length - 1 : Math.floor(Math.random() * (MOVES.length - 1)));
      this.moveT = 0;
      this.nextFidget = 3 + Math.random() * 5;
    }
    const pose = this.pose;
    const theta = ((this.swayNow + pose.turn + this.bend.value) * Math.PI) / 180;
    // Its flair as a stage's figure steps it: it stands still, so a turn and a hop are what its tails swing from.
    this.flair?.step(dt, {
      x: Math.sin(theta) * SWING,
      y: pose.y * GARDENER.body.size[1],
      facing: 1,
      body: bodyPose('idle', Math.floor(this.clock / LOBBY.frameS)),
      gun: this.worn.gun === 'stock' ? null : { ...HOLD, aim: 0 },
      team: 'blue',
    });
  }

  /**
   * The lobby: the backdrop, then the figure as `GardenerFigure` draws it (its idle frame in slices stacked in depth,
   * the stack turned round its vertical axis: each slice's x is sin θ · z and its width cos θ, the back ones darker),
   * the gun in its fist on the stack's near face, its flair round it (what is behind it in front while it is turned
   * away), and the dust in the beam.
   */
  private drawLobby(me: GardenerArt): void {
    const g = this.g;
    const { height } = this.canvas;
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.globalAlpha = 1;
    g.globalCompositeOperation = 'source-over';
    if (this.backdrop) g.drawImage(this.backdrop, 0, 0);
    const { x, y, k, h } = this.stand;
    const pose = this.pose;
    const theta = ((this.swayNow + pose.turn + this.bend.value) * Math.PI) / 180;
    const [cos, sin] = [Math.cos(theta), Math.sin(theta)];
    const d = GARDENER.body.density;
    // World px round the feet, the move's hop, squash and lean on top (the lean a drag gives with it).
    g.setTransform(k * d, 0, 0, k * d, x, y + pose.y * h);
    g.rotate(((pose.rot + this.bend.value * LOBBY.lean) * Math.PI) / 180);
    g.scale(pose.sx, pose.sy);
    g.imageSmoothingEnabled = true;
    g.imageSmoothingQuality = 'high';
    const frame = Math.floor(this.clock / LOBBY.frameS) % me.idle.length;
    const depth = (LOBBY.depth * GARDENER.body.size[1]) / d;
    const away = cos < 0;
    const slices = () => {
      for (let j = 0; j < LOBBY.slices; j++) {
        const f = j / (LOBBY.slices - 1);
        const z = depth * (away ? 0.5 - f : f - 0.5);
        const front = j === LOBBY.slices - 1;
        const a = front
          ? pick(me.idle, frame)
          : me.shade(frame, Math.min(SHADES.length - 1, Math.floor(f * SHADES.length)));
        this.drawArt(g, a, sin * z, 0, { sx: cos });
      }
    };
    const flair = (layer: number) => this.drawFlair(layer, 0, 0, cos / d, 1 / d);
    const gun = () => {
      // On the stack's near face, turning with it.
      const z = (depth / 2) * Math.sign(cos || 1);
      g.save();
      g.translate((HOLD.x / d) * cos + sin * z, HOLD.y / d);
      g.scale(cos, 1);
      const fps = this.fx ? fpsOf(this.fx.held, 'idle') : 0;
      this.drawArt(g, pick(this.gun, fps ? Math.floor(this.clock * fps) % this.gun.length : 0), 0, 0);
      const [ox, oy] = pollinator.origin;
      if (this.counter) {
        const [x0, y0, x1, y1] = COUNTER_BOX;
        // Mirrored with the gun (turned away), it turns back to read.
        this.drawArt(g, this.counter, ((x0 + x1) / 2 - ox) / d, ((y0 + y1) / 2 - oy) / d, { sx: Math.sign(cos || 1) });
      }
      const [hx, hy] = pollinator.hand;
      this.drawArt(g, me.hand, (hx - ox) / d, (hy - oy) / d);
      g.restore();
    };
    const parts = [() => flair(0), slices, () => flair(1), gun, () => flair(2)];
    // Turned away, the gun is behind the body and what its flair draws behind it is in front.
    for (const part of away ? [gun, () => flair(2), () => flair(1), slices, () => flair(0)] : parts) part();
    // Dust drifting down the beam, twinkling.
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.fillStyle = '#fff4d6';
    for (const m of this.motes) {
      m.t = (m.t + 0.0008) % 1;
      const half = (0.2 + 0.75 * m.t) * h * 0.8;
      const a = 0.4 * Math.max(0, Math.sin(this.clock * 1.3 + m.p)) * Math.sin(Math.PI * m.t);
      if (a < 0.01) continue;
      g.globalAlpha = a;
      g.beginPath();
      g.arc(
        x + (m.s + Math.sin(this.clock * 0.7 + m.p) * 0.06) * half,
        y * m.t,
        Math.max(1, height / 260),
        0,
        Math.PI * 2,
      );
      g.fill();
    }
    g.globalAlpha = 1;
  }

  /**
   * The default gun's hot streak with its head at (x, y), `length` px long (`HotStreak.draw`), `head`: its head too (a
   * skin's bullet is its own); in the skin's colour and look.
   */
  private drawStreak(x: number, y: number, ux: number, uy: number, length: number, alpha: number, head: boolean): void {
    const g = this.g;
    const k = STREAK;
    const color = this.fx?.tracer ?? GOLD;
    const look = this.look;
    const tipColor = look.tip ?? 0xffffff;
    const [tx, ty] = [x - ux * length, y - uy * length];
    const along = (c: number, a: number, light = 0, x0 = tx, y0 = ty) => {
      const gr = g.createLinearGradient(x0, y0, x, y);
      gr.addColorStop(0, rgbaOf(c, 0, light));
      gr.addColorStop(1, rgbaOf(c, a * alpha, light));
      return gr;
    };
    const line = (width: number, style: CanvasGradient, x0 = tx, y0 = ty) => {
      g.lineWidth = width;
      g.strokeStyle = style;
      g.beginPath();
      g.moveTo(x0, y0);
      g.lineTo(x, y);
      g.stroke();
    };
    // Its glow (soft across: a wide faint stroke under a narrower one), added on.
    g.globalCompositeOperation = 'lighter';
    line(k.glow, along(color, k.glowAlpha * 0.45));
    line(k.glow / 2, along(color, k.glowAlpha * 0.6));
    g.globalCompositeOperation = 'source-over';
    // A wedge of a core, lighter, from a point at its tail to its head.
    const [nx, ny] = [(-uy * k.core) / 2, (ux * k.core) / 2];
    g.fillStyle = look.core === undefined ? along(color, 1, k.coreLight) : along(look.core, 1);
    g.beginPath();
    g.moveTo(tx, ty);
    g.lineTo(x + nx, y + ny);
    g.lineTo(x - nx, y - ny);
    g.closePath();
    g.fill();
    const tip = Math.min(length, k.tip);
    line(1, along(tipColor, 1, 0, x - ux * tip, y - uy * tip), x - ux * tip, y - uy * tip);
    if (!head) return;
    g.fillStyle = rgbaOf(tipColor, alpha);
    g.beginPath();
    g.arc(x, y, k.head / 2, 0, Math.PI * 2);
    g.fill();
  }

  /** Mote `e` as `StreakFx.place` draws it (`viewMote`): its mask in its colour, turned, added on unless solid. */
  private drawEmber(e: Mote): void {
    const g = this.g;
    const v = viewMote(e, this.moteView);
    if (v.alpha <= 0 || v.w <= 0 || v.h <= 0) return;
    g.save();
    g.translate(v.x, v.y);
    g.rotate(v.rotation);
    g.globalAlpha = Math.min(1, v.alpha);
    g.globalCompositeOperation = v.solid ? 'source-over' : 'lighter';
    g.drawImage(moteIn(v.shape, hex(v.tint)), -v.w / 2, -v.h / 2, v.w, v.h);
    g.restore();
  }

  /**
   * The default gun's shots: its embers, then each hot streak and the tails of the spent (`BulletFx`, `StreakFx`); an
   * effect skin's in its look, its bullet riding on each (`skin`).
   */
  private drawStreaks(skin: SkinFx | null): void {
    const g = this.g;
    g.globalAlpha = 1;
    for (const e of this.embers) this.drawEmber(e);
    g.globalAlpha = 1;
    g.globalCompositeOperation = 'source-over';
    for (const b of this.shots) {
      const length = Math.min(b.flown + 4, W.projectileSpeed * STREAK.seconds);
      const [ux, uy] = [b.vx / W.projectileSpeed, b.vy / W.projectileSpeed];
      this.drawStreak(b.x, b.y - AIM_HEIGHT, ux, uy, length, 1, !skin);
    }
    for (const s of this.spent) {
      const k = 1 - s.t / s.dur;
      this.drawStreak(s.x, s.y - AIM_HEIGHT, s.ux, s.uy, s.length * k, 0.4 + 0.6 * k, false);
    }
  }

  /** Bullets: the default gun's hot streaks, or the skin's in its look with its shot riding each over its glow. */
  private drawShots(): void {
    const g = this.g;
    const skin = this.fx;
    this.drawStreaks(skin);
    if (!skin) return;
    const glow = pick(art('skin_glow', 'idle', skin.glow), 0);
    const look = art(skin.bullet, 'idle');
    const fps = fpsOf(skin.bullet, 'idle');
    for (const b of this.shots) {
      const y = b.y - AIM_HEIGHT;
      const alpha = 0.16 + 0.06 * Math.sin(b.age * 60);
      this.drawArt(g, glow, b.x, y, { sx: 0.625, sy: 0.625, alpha, add: true, centre: true });
      const rot = skin.upright ? 0 : Math.atan2(b.vy, b.vx);
      this.drawArt(g, pick(look, Math.floor(b.age * fps) % look.length), b.x, y, { rot });
    }
  }

  private drawBit(p: Bit): void {
    const t = Math.min(1, p.age / p.life);
    const k = p.scaleFrom + (p.scaleTo - p.scaleFrom) * t;
    const alpha = p.landed
      ? p.alpha * Math.max(0, 1 - Math.max(0, p.resting - p.rest) / 0.6)
      : p.fade
        ? p.alpha * (1 - t * t)
        : p.alpha;
    const o = { rot: p.rotation, sx: k, sy: k, alpha, add: p.add, centre: p.centre };
    this.drawArt(this.g, pick(p.art, p.frame), p.x, p.y - p.z, o);
  }

  /** `a` with its origin (or its middle: `centre`) at (x, y) in `g`'s units (world px), turned, scaled, faded, added. */
  private drawArt(
    g: CanvasRenderingContext2D,
    a: Art,
    x: number,
    y: number,
    o: { rot?: number; sx?: number; sy?: number; alpha?: number; add?: boolean; centre?: boolean } = {},
  ): void {
    g.save();
    g.translate(x, y);
    if (o.rot) g.rotate(o.rot);
    g.scale((o.sx ?? 1) / a.d, (o.sy ?? 1) / a.d);
    g.globalAlpha = o.alpha ?? 1;
    if (o.add) g.globalCompositeOperation = 'lighter';
    const [ax, ay] = o.centre ? [a.w / 2, a.h / 2] : [a.ox, a.oy];
    g.drawImage(a.img, -ax, -ay, a.w, a.h);
    g.restore();
  }
}
